<template>
  <div
    id="notificationDropDownWrapper"
    class="header-item"
  >
    <el-badge
      id="notificationDropDown"
      :value="notifications.newCount"
      :hidden="notifications.newCount < 1 || previewMode"
    >
      <div
        v-if="$mq === 'laptop' || $mq === 'desktop'"
        id="bigNotify"
      >
        <el-popover
          ref="popover"
          placement="bottom-end"
          width="400"
          transition="el-zoom-in-bottom"
          popper-class="notificationList"
          :offset="50"
          :disabled="previewMode"
          @show="loadNotifications"
        >
          <el-button
            slot="reference"
            plain
            icon="el-icon-bell"
            class="expandBtn"
          />
          <div
            v-if="$refs.popover && $refs.popover.showPopper
              && notifications.notifications.length > 0"
            class="content"
          >
            <div
              v-infinite-scroll="fetchNextNotifications"
              class="infinite-list notification-scroll-area"
            >
              <div
                v-for="(notification, key) in notifications.notifications"
                :key="key"
                class="notification"
              >
                <Notification
                  :notification="notification"
                  @showModal="showNotificationModal(notification)"
                  @click="$refs.popover.doToggle"
                />
              </div>
            </div>

            <div class="markAllRead">
              <el-button
                type="text"
                @click="markAllAsRead"
              >
                {{ $t('notification.markAllAsRead') }}
              </el-button>
            </div>
          </div>
          <div
            v-else
            v-loading="loading"
            class="noData"
          >
            <span
              v-if="!loading"
              class="defaultCursor"
            >
              <i class="el-icon-warning"></i> {{ $t('notification.noData') }}
            </span>
          </div>
        </el-popover>
      </div>

      <div v-else>
        <el-button
          slot="reference"
          plain
          icon="el-icon-bell"
          class="expandBtn"
          @click="drawer = !drawer"
        />
        <el-drawer
          id="compactNotify"
          :title="$t('notification.notifications')"
          direction="rtl"
          size="80%"
          :visible.sync="drawer"
          :modal="true"
          :modal-append-to-body="false"
          @open="loadNotifications"
        >
          <div
            v-if="drawer && notifications.notifications.length > 0"
            id="compactContent"
            class="compactContent"
          >
            <div
              id="notificationArea"
              v-infinite-scroll="fetchNextNotifications"
              class="infinite-list notification-scroll-area"
              :class="setManager"
            >
              <div
                v-for="(notification, key) in notifications.notifications"
                :key="key"
                class="notification"
              >
                <Notification
                  :notification="notification"
                  @showModal="showNotificationModal(notification)"
                  @click="drawer = false"
                />
              </div>
            </div>

            <div class="markAllRead">
              <el-button
                type="text"
                @click="markAllAsRead"
              >
                {{ $t('notification.markAllAsRead') }}
              </el-button>
            </div>
          </div>
          <div
            v-else
            v-loading="loading"
            class="noData"
          >
            <span><i class="el-icon-warning"></i> {{ $t('notification.noData') }}</span>
          </div>
        </el-drawer>
      </div>
    </el-badge>

    <NotificationModal
      :show.sync="showModal"
      :notification.sync="selectedNotification "
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import { EventSourcePolyfill } from 'event-source-polyfill';
import Notification from './Notification.vue';
import NotificationModal from './NotificationModal.vue';

export default {
  components: {
    Notification,
    NotificationModal,
  },

  props: {
    previewMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showModal: false,
      selectedNotification: null,
      drawer: false,
      loading: false,
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      notificationsVuex: (state) => state.notifications,
    }),

    notifications() {
      if (this.previewMode) {
        return {
          newCount: 5,
        };
      }
      return this.notificationsVuex;
    },

    setManager() {
      let mode = 'normal';
      if (['mobile', 'tablet'].includes(this.$mq) && window.innerHeight > window.innerWidth) {
        mode = 'shortMode';
      } else {
        mode = 'longMode';
      }
      return mode;
    },
  },

  mounted() {
    // don t init SSE if front is localhost and backend is dev server
    if (process.env.VUE_APP_ENV !== 'local' && !this.previewMode) {
      setTimeout(() => {
        this.initSSEClient();
      }, 500);
    }
    window.addEventListener('unload', this.closeSSEClient);
  },

  beforeDestroy() {
    console.log('beforeDestroy');
    this.closeSSEClient();
  },

  methods: {
    ...mapActions('notifications', [
      'fetchNotifications',
      'fetchNextNotifications',
      'markAsRead',
      'markAllAsRead',
    ]),

    ...mapMutations('notifications', [
      'addOneNotification',
    ]),

    ...mapMutations('coachAlerts', [
      'addOneAlert',
    ]),

    showNotificationModal(notification) {
      this.selectedNotification = notification;
      this.showModal = true;
    },

    async loadNotifications() {
      this.loading = true;
      this.customScrollStyle = { height: '350px' };
      await this.fetchNotifications();
      this.customScrollStyle = null;
      this.loading = false;
    },

    initSSEClient() {
      try {
        this.client = new EventSourcePolyfill(
          `${process.env.VUE_APP_API_HOST}/events/user/${this.user.user.uuid}/`,
          {
            withCredentials: true,
          },
        );

        this.client.onopen = () => {
          console.log('SSE connected');
        };
        this.client.onclose = () => {
          console.log('SSE disconnected');
        };
        this.client.onerror = (e) => {
          console.log('SSE onerror:', e);
        };
        this.client.onmessage = (e) => this.handleNewNotification(e);
      } catch (err) {
        console.log('SSE connection error:', err);
      }
    },

    closeSSEClient() {
      console.log('Attempt to close SSE client');
      if (this.client && this.client.readyState !== 2) {
        try {
          console.log('Actually closing SSE client');
          this.client.close();
        } catch (err) {
          console.log('SSE error onClosing:', err);
        }
      }
    },

    handleNewNotification(e) {
      const data = JSON.parse(e.data);

      // TODO: Change way to differentiate alert and notification
      if (data.is_alert) {
        if (data.data) {
          this.addOneAlert({
            type: data.type,
            data: data.data,
          });
        }
        if (data.action === 'no_meeting_cancel') {
          this.$notify({
            title: `ALERT: A new ${data.type} is waiting for your confirmation`,
            offset: 50,
            iconClass: 'el-icon-bell',
          });
        }
      } else if (data.title.search('chosen to coach') > -1 && data.leader_name) {
        this.addOneNotification({ notification: data });
        this.$notify({
          title: data.title,
          message: `Leader name: ${data.leader_name}`,
          offset: 50,
          iconClass: 'el-icon-bell',
        });
      } else {
        this.addOneNotification({ notification: data });
        this.$notify({
          title: data.title,
          message: data.description,
          offset: 50,
          iconClass: 'el-icon-bell',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  #notificationDropDownWrapper {
    display: inline-block;

    #notificationDropDown {
      position: relative;
      top: -1px;

      .expandBtn {
        background: var(--themeCardColor);
        color: var(--themeFontColor);
        border-width: 0;
      }
    }
  }
</style>

<style lang="scss">
  .el-popover.notificationList {
    padding: 0;

    .content {
      .notification-scroll-area {
        position: relative;
        margin: auto;
        max-height: 350px;
        overflow-y: auto;
      }

      .notification {
        &:not(:last-child) {
          border-bottom: 1px solid #ebeef5;
        }
      }

      .markAllRead {
        width: 100%;
        background-color: white;
        border-top: 1px solid #ebeef5;

        .el-button {
          width: 100%;
          height: 50px;
        }
      }
    }

    .noData {
      height: 250px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: center;
      font-style: italic;
      font-size: 16px;
      font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
    }
  }

  #compactNotify {
    .el-drawer__container {
      overflow: hidden;

      .rtl {
        // Prevent reversed notification from calendar
        direction: ltr !important;
      }
    }

    .el-drawer {
      text-align: left !important;

      .el-drawer__header {
        padding: 20px 10px;
        margin-bottom: 0;
        border-bottom: 2px solid #ebeef5;

        > span:focus {
          outline: none !important; // fix drawer bug in element 2.13.0, remove when fixed
        }
      }
    }

    .el-drawer__body {
      .compactContent {
        .notification-scroll-area {
          position: relative;
          margin: auto;
          overflow-y: auto;
          height: calc(var(--vh, 1vh) * 100 - 180px);
          box-sizing: border-box;

          &.shortMode {
            height: calc(var(--vh, 1vh) * 100 - 185px);
          }
        }

        .notification {
          &:not(:last-child) {
            border-bottom: 1px solid #ebeef5;
          }

          .notificationItem {
            padding: 7px 10px;
            box-sizing: border-box;
          }
        }

        .markAllRead {
          width: 100%;
          background-color: white;
          border-top: 1px solid #ebeef5;

          .el-button {
            width: 100%;
            height: 50px;
          }
        }
      }
    }

    .noData {
      width: 100%;
      text-align: center;
      padding: 20px;
      color: #606266;
      font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
    }
  }
</style>
